import {
  QueryFieldFilterConstraint,
  QueryLimitConstraint,
  QueryOrderByConstraint,
  QueryStartAtConstraint,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
} from 'firebase/firestore'

import { FirebaseCollection } from '@/constants/firebase-collection'
import { firestore } from '@/services/firebase'
import { delay } from '@/utils/tricks'

interface GetListProps {
  companyId: string
  setStockRequestList: (stockRequestList: StockRequest[] | undefined) => void
  setLoadingList: React.Dispatch<React.SetStateAction<boolean>>
  whereList?: QueryFieldFilterConstraint[]
  orderByList?: QueryOrderByConstraint[]
  startAfter?: QueryStartAtConstraint[]
  limit?: QueryLimitConstraint[]
  timeDelay?: number
}
export const getList = async ({
  companyId,
  setStockRequestList,
  setLoadingList,
  whereList,
  orderByList,
  startAfter,
  limit,
  timeDelay,
}: GetListProps) => {
  setLoadingList(true)

  if (timeDelay) await delay(timeDelay)

  const q = query(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_STOCK_REQUEST,
    ),
    ...(whereList || []),
    ...(orderByList || []),
    ...(startAfter || []),
    ...(limit || []),
  )

  onSnapshot(
    q,
    querySnapshot => {
      const stockRequestList: StockRequest[] = []
      querySnapshot.forEach(doc => {
        const dataItem = { id: doc.id, ...doc.data() } as StockRequest
        stockRequestList.push(dataItem)
      })
      setLoadingList(false)
      setStockRequestList(stockRequestList)
    },
    () => {
      console.log('stockRequest - getList() - error')
      setLoadingList(false)
    },
  )
}

interface GetOneProps {
  companyId: string
  stockRequestId: string
}
export const getOne = async ({
  companyId,
  stockRequestId,
}: GetOneProps): Promise<StockRequest | undefined> => {
  const docRef = doc(
    firestore,
    FirebaseCollection.BASE_COMPANY,
    companyId,
    FirebaseCollection.BASE_STOCK_REQUEST,
    stockRequestId,
  )
  const docSnap = await getDoc(docRef)

  try {
    if (docSnap.exists()) {
      return {
        id: stockRequestId,
        ...docSnap.data(),
      } as StockRequest
    }
  } catch (error) {
    return undefined
  }

  return undefined
}

interface SaveProps {
  companyId: string
  values: Partial<StockRequest>
}
export const save = async ({ companyId, values }: SaveProps) =>
  await addDoc(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_STOCK_REQUEST,
    ),
    {
      ...values,
      titleSearch: `${(values.title || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      supplierNameSearch: `${(values.supplierName || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      createdAt: new Date().getTime(),
      dateCreatedAt: new Date().setHours(0, 0, 0, 0),
      updatedAt: new Date().getTime(),
    },
  ).then(resp => resp.id)

interface UpdateProps {
  companyId: string
  stockRequestId: string
  values: Partial<StockRequest>
}
export const update = async ({
  companyId,
  stockRequestId,
  values,
}: UpdateProps) => {
  await setDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_STOCK_REQUEST,
      stockRequestId,
    ),
    {
      ...values,
      titleSearch: `${(values.title || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      supplierNameSearch: `${(values.supplierName || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      updatedAt: new Date().getTime(),
    },
    { merge: true },
  )
}

interface RemoveProps {
  companyId: string
  stockRequestId: string
}
export const remove = async ({ companyId, stockRequestId }: RemoveProps) => {
  await deleteDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_STOCK_REQUEST,
      stockRequestId,
    ),
  )
}
